// chat/Chat.tsx — the chat panel UI (cloned from Oriora Expense's src/chat/Chat.tsx). Purely
// ADDITIVE: it drives the SAME capabilities the app already has (log a fill-up/service, add a
// vehicle, find entries, narrate/ask your numbers) by typing. All styling is namespaced under
// .grc-* so it can't collide with the existing app CSS.
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit Confirm
// tap (a chat message can't destroy data on its own) — mirrors Expense's discipline.

import { useState, useRef, useEffect } from "react";
import { askBrain, type ChatTurn, type ChatContext } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store, fmtDate } from "../store";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type FilterRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

const STORE_KEY = "oriora.garage.chat.v1"; // device-only; lost on clear/reinstall

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    'Tell me what to do — log a fill-up or service, add a vehicle, find something in your log, or ask about your numbers.\n\n' +
    'e.g. "add fillup 42350 mi, 12.4 gal, $46.80" · "log service oil change at 42000 mi, $60" · "how much has this car cost me this year?"',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
// (backup_restore + import_file REPLACE data — previously auto-run; now held too.)
const destructive = (k: ChatAction["kind"]) => k === "delete_fillup" || k === "delete_service" || k === "delete_vehicle" || k === "backup_restore" || k === "import_file" ||
        k === "library_remove" || k === "library_merge";
const warnFor = (action: ChatAction): string =>
  
  action.kind === "library_merge"
    ? `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : action.kind === "library_remove"
      ? `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : action.kind === "import_file"
    ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
    : action.kind === "backup_restore"
      ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
      : action.kind === "delete_vehicle"
      ? `Delete ${action.label ?? "this vehicle"} and ALL of its fill-ups and service records? This can't be undone — tap Confirm, or Cancel to keep it.`
      : `Delete ${(action.kind === "delete_fillup" || action.kind === "delete_service") ? (action.label ?? "this entry") : "this"}? This can't be undone — tap Confirm, or Cancel to keep it.`;
const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";
type PendingHold = { action: ChatAction; rest: ChatAction[] };

export default function Chat({ vehicleId, vehicleName, onChanged }: {
  vehicleId: string | null;
  vehicleName?: string | null;
  onChanged?: () => void;
}) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A delete is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  // A photo picked in the composer, waiting to be filed onto a fill-up. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  const ctx: ChatContext = { vehicleId, vehicleName: vehicleName ?? null };
  const transcriptOf = (t: ChatTurn[]) => t.map((x) => `${x.role}: ${x.content}`).join("\n");

  async function runQueue(queue: ChatAction[], transcript: string): Promise<{ lines: string[]; rows?: FilterRow[]; changed: boolean; hold: PendingHold | null }> {
    const lines: string[] = [];
    let rows: FilterRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action.kind)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1) } };
      }
      const result = await executeAction(action, { transcript });
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  // A photo turn is filed ON-DEVICE onto a fill-up (a receipt/proof photo): no model call, no cost.
  // Default target = the latest fill-up; a note word in the same message can pick a specific one.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      if (!vehicleId) {
        setTurns([...next, { role: "assistant", content: "Pick or add a vehicle first — then I'll file the photo onto its fill-up." }]);
        return; // keep the photo attached
      }
      const fillups = await store.listFillups(vehicleId, 50000); // newest-first (see db.rs)
      if (fillups.length === 0) {
        setTurns([...next, { role: "assistant", content: "Log a fill-up first, then I'll attach the receipt photo to it." }]);
        return; // keep the photo attached so the next message can name/log the fill-up
      }
      const q = text.trim().toLowerCase().replace(/[.?!]+$/, "");
      const isLatest = !q || /^(last|latest|recent|most\s+recent)$/.test(q);
      const matches = isLatest ? [] : fillups.filter((f) => f.note.toLowerCase().includes(q));
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((f) => `• ${fmtDate(f.ts)} — ${f.note || "(no note)"}`).join("\n");
        setTurns([...next, { role: "assistant", content: `More than one fill-up matches — which one?\n${list}` }]);
        return; // keep the photo attached
      }
      const target = matches.length === 1 ? matches[0] : fillups[0]; // else default to the latest
      await store.updateFillup(target.id, { photo: photo! });
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `📷 Attached your photo to the ${fmtDate(target.ts)} fill-up.` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, ctx, onChunk);
      // Run every non-destructive action in order — two asks in one message must do BOTH (G17).
      const r = await runQueue(actions, transcriptOf(next));
      if (r.changed) onChanged?.();
      if (r.hold) {
        setPending(r.hold);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warnFor(r.hold.action) + tailNote(r.hold.rest)].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines].filter(Boolean).join("\n\n") || "Done.", rows: r.rows }]);
      }
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    const { action, rest } = pending;
    try {
      const lines: string[] = [];
      let rows: FilterRow[] | undefined;
      const result = await executeAction(action, { transcript: transcriptOf(turns) });
      if (result.message) lines.push(result.message);
      if (result.changed) onChanged?.();
      const r = await runQueue(rest, transcriptOf(turns));
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      if (r.changed) onChanged?.();
      if (r.hold) { setPending(r.hold); lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest)); }
      else setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: lines.filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." + (skipped ? ` (Skipped the ${skipped} step${skipped > 1 ? "s" : ""} that were waiting on this.)` : " Tell me what else to do.") }]);
  }

  return (
    <div className="grc-chat">
      <div className="grc-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`grc-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`grc-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="grc-msgrow"><div className="grc-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="grc-composer grc-confirm">
          <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={confirmPending} disabled={busy}>{pending && String(pending.action.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="grc-composer">
            <textarea
              value={input}
              placeholder={photo ? "Name the fill-up, or just send…" : "Tell me what to do…"}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            <button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
            <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
          </div>
        </>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="grc-rows">
      {rows.map((r) => (
        <div key={r.id} className="grc-rowitem">
          <span>{r.date_on} · {r.label}</span>
          <span className="tnum muted">{r.detail}</span>
        </div>
      ))}
    </div>
  );
}
